import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { Globe, ShoppingCart, Server, Building2, Bot, Layers, Plus, ArrowRight } from "lucide-react";
import Reveal, { SplitTitle, EASE } from "../components/ui/Reveal";
import ChallengeBox from "../components/common/ChallengeBox";
import useLang from "../hooks/useLang";

const CATEGORIES = [
  { key: "web", icon: Globe },
  { key: "ecommerce", icon: ShoppingCart },
  { key: "backend", icon: Server },
  { key: "erp", icon: Building2 },
  { key: "ai", icon: Bot },
  { key: "more", icon: Layers },
];

// One solution row: the title toggles its description and an "ask about this" link.
const OfferingRow = ({ offering, open, onToggle, askTo, askLabel, isRTL }) => (
  <li className="border-t border-zinc-200 last:border-b">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`w-full flex items-center justify-between gap-4 py-4 text-start font-bold transition-colors ${
        open ? "text-rose-dark" : "text-ink hover:text-rose-dark"
      }`}
    >
      {offering.title}
      <Plus
        size={18}
        className={`flex-shrink-0 text-rose transition-transform duration-300 ease-out-expo ${open ? "rotate-45" : ""}`}
      />
    </button>
    <div
      className={`grid transition-[grid-template-rows] duration-500 ease-out-expo ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      }`}
    >
      <div className="overflow-hidden">
        <p className="text-zinc-500 leading-relaxed pb-2">{offering.desc}</p>
        <Link to={askTo} className="link-arrow group text-rose-dark hover:text-rose pb-5" tabIndex={open ? 0 : -1}>
          {askLabel}
          <ArrowRight
            size={14}
            className={`transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180`}
          />
        </Link>
      </div>
    </div>
  </li>
);

const Services = () => {
  const { t, isRTL, dir } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("category");
  const initial = CATEGORIES.some((c) => c.key === requested) ? requested : CATEGORIES[0].key;

  const [active, setActive] = useState(initial);
  const [openRow, setOpenRow] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(initial);

  // Follow links that change ?category= while the page is already open (search, footer).
  useEffect(() => {
    if (CATEGORIES.some((c) => c.key === requested)) {
      setActive(requested);
      setMobileOpen(requested);
      setOpenRow(0);
    }
  }, [requested]);

  const select = (key) => {
    if (key === active) return;
    setActive(key);
    setOpenRow(0);
    setSearchParams({ category: key }, { replace: true, preventScrollReset: true });
  };

  const data = (key) => ({
    title: t(`whatWeDo.categories.${key}.title`),
    desc: t(`whatWeDo.categories.${key}.desc`),
    offerings: t(`whatWeDo.categories.${key}.offerings`, { returnObjects: true }),
  });
  const total = CATEGORIES.reduce((n, c) => n + data(c.key).offerings.length, 0);
  const askTo = (key, title) =>
    `/contact?service=${CATEGORIES.findIndex((c) => c.key === key)}&topic=${encodeURIComponent(title)}`;

  const current = data(active);
  const activeIndex = CATEGORIES.findIndex((c) => c.key === active);

  return (
    <div dir={dir} className="font-inter">
      {/* Header */}
      <section className="pt-36 pb-12 bg-white">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <span className="eyebrow text-rose-dark">
              {t("nav.services")} · {total}
            </span>
            <h1 className="h-section font-extrabold text-ink mt-4">{t("whatWeDo.title")}</h1>
            <p className="lead text-zinc-500 mt-4">{t("whatWeDo.subtitle")}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="container-site">
          {/* Desktop: sticky index + detail panel */}
          <div className="hidden lg:grid grid-cols-12 gap-10 items-start">
            <nav className="col-span-5 sticky top-[104px]" aria-label={t("nav.services")}>
              <ol>
                {CATEGORIES.map(({ key, icon: Icon }, i) => {
                  const on = key === active;
                  return (
                    <li key={key} className="border-t border-zinc-200 last:border-b">
                      <button
                        type="button"
                        onClick={() => select(key)}
                        aria-current={on ? "true" : undefined}
                        className={`group w-full flex items-center gap-4 py-5 text-start transition-[padding] duration-500 ease-out-expo ${
                          on ? "ps-3" : "ps-0"
                        }`}
                      >
                        <span className="font-mono text-[11px] text-rose w-6">0{i + 1}</span>
                        <span
                          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${
                            on ? "bg-ink text-rose-light" : "bg-zinc-100 text-ink"
                          }`}
                        >
                          <Icon size={18} />
                        </span>
                        <span
                          className={`text-[22px] font-extrabold tracking-[-0.02em] leading-tight transition-colors duration-500 ${
                            on ? "text-rose" : "text-zinc-400 group-hover:text-ink"
                          }`}
                        >
                          {data(key).title}
                        </span>
                        <span className="ms-auto text-sm text-zinc-400">{data(key).offerings.length}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>

            <div className="col-span-7 rounded-3xl bg-paper p-8 xl:p-10 min-h-[560px]">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                <span className="font-mono text-[11px] tracking-[0.14em] text-zinc-400">
                  0{activeIndex + 1} · {current.offerings.length}
                </span>
                <h2 className="text-3xl font-extrabold text-ink tracking-[-0.02em] mt-2">{current.title}</h2>
                <p className="text-zinc-500 mt-2 mb-6">{current.desc}</p>
                <ul>
                  {current.offerings.map((o, i) => (
                    <OfferingRow
                      key={o.title}
                      offering={o}
                      open={openRow === i}
                      onToggle={() => setOpenRow(openRow === i ? -1 : i)}
                      askTo={askTo(active, o.title)}
                      askLabel={t("whatWeDo.ask")}
                      isRTL={isRTL}
                    />
                  ))}
                </ul>
                <Link to="/contact" className="btn-rose group mt-8">
                  {t("cta.button")}
                  <ArrowRight size={15} className={`transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180`} />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Mobile and tablet: index rows open in place */}
          <ol className="lg:hidden">
            {CATEGORIES.map(({ key, icon: Icon }, i) => {
              const d = data(key);
              const on = mobileOpen === key;
              return (
                <li key={key} className="border-t border-zinc-200 last:border-b">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(on ? null : key);
                      setOpenRow(-1);
                    }}
                    aria-expanded={on}
                    className="w-full flex items-center gap-3 py-5 text-start"
                  >
                    <span className="font-mono text-[11px] text-rose w-6">0{i + 1}</span>
                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${on ? "bg-ink text-rose-light" : "bg-zinc-100 text-ink"}`}>
                      <Icon size={17} />
                    </span>
                    <span className={`text-lg font-extrabold tracking-[-0.01em] ${on ? "text-rose" : "text-ink"}`}>{d.title}</span>
                    <span className="ms-auto text-sm text-zinc-400">{d.offerings.length}</span>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-500 ease-out-expo ${on ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="text-zinc-500 text-sm pb-3">{d.desc}</p>
                      <ul className="pb-6">
                        {d.offerings.map((o, k) => (
                          <OfferingRow
                            key={o.title}
                            offering={o}
                            open={on && openRow === k}
                            onToggle={() => setOpenRow(openRow === k ? -1 : k)}
                            askTo={askTo(key, o.title)}
                            askLabel={t("whatWeDo.ask")}
                            isRTL={isRTL}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* CTA: visitors who didn't find their solution describe their challenge instead */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="pointer-events-none absolute -top-32 end-[-10%] h-[480px] w-[480px] rounded-full bg-rose/15 blur-[120px]" aria-hidden="true" />
        <div className="relative container-site py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <SplitTitle text={t("cta.title")} className="h-section" />
            <p className="lead text-white/60 mt-4">{t("cta.description")}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <ChallengeBox />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Services;
